import { Outlet, createFileRoute, notFound } from '@tanstack/react-router';

import { getHoofdstukReserveringCollection } from '@/api/collections/hoofdstuk-reservering/hoofdstuk-reservering';
import { labelCollection } from '@/api/collections/labels/label';
import { labelMapCollection } from '@/api/collections/labels/label-map';
import { getVoorschriftLabelCollection } from '@/api/collections/labels/voorschrift-label';
import { useLatestBedrijf } from '@/api/queries/bedrijf';
import { useBesluitId } from '@/api/queries/besluit';
import { useBesluitTypeId } from '@/api/queries/besluitType';
import { DocumentTitle } from '@/components/DocumentTitle';
import { eStrings } from '@/constants/strings';
import { isUserAuthenticatedForRoute } from '@/stores/user/isUserAuthenticatedForRoute';

import { Navigation } from './-components/Navigation/Navigation';
import { SidebarLeft } from './-components/SidebarLeft/SidebarLeft';
import { useEditorStore } from './-store/useEditorStore';
import {
  ensureHoofdstukkenInEditorQueryData,
  useHoofdstukkenInEditor,
} from './-utils/useHoofdstukkenInEditor';

/**
 * Route
 */
export const Route = createFileRoute('/$bedrijfLineageId/editor/$besluitId')({
  beforeLoad: async ({ preload, context, params: { besluitId } }) => {
    await isUserAuthenticatedForRoute({
      preload,
      allowed: ['Vergunningverlener'],
    });

    const besluit = await context.queryClient.ensureQueryData(
      useBesluitId.getOptions(besluitId)
    );

    return {
      voorschriftLabelCollection: getVoorschriftLabelCollection({
        besluitLineageId: besluit.Lineage,
      }),
      hoofdstukReserveringCollection: getHoofdstukReserveringCollection({
        besluitLineageId: besluit.Lineage,
      }),
    };
  },
  loader: async ({
    context: {
      queryClient,
      voorschriftLabelCollection,
      hoofdstukReserveringCollection,
    },
    params: { besluitId, bedrijfLineageId },
  }) => {
    const [besluit, latestBedrijf] = await Promise.all([
      queryClient.ensureQueryData(useBesluitId.getOptions(besluitId)),
      queryClient.ensureQueryData(
        useLatestBedrijf.getOptions(bedrijfLineageId)
      ),
      ensureHoofdstukkenInEditorQueryData({ besluitId, queryClient }),
      labelMapCollection.preload(),
      labelCollection.preload(),
      voorschriftLabelCollection.preload(),
      hoofdstukReserveringCollection.preload(),
    ]);

    await queryClient.ensureQueryData(
      useBesluitTypeId.getOptions(besluit.Type)
    );

    if (!besluit || !latestBedrijf.objects?.[0]) throw notFound();
  },
  onLeave: () => {
    // Reset the editor state when the user leaves the editor
    const { reset } = useEditorStore.getState();
    reset();
  },
  component: EditorComponent,
});

/**
 * Route component
 */
function EditorComponent() {
  const { besluitId, bedrijfLineageId } = Route.useParams();

  const besluit = useBesluitId(besluitId);
  const bedrijf = useLatestBedrijf(bedrijfLineageId);
  const besluitType = useBesluitTypeId(besluit.data?.Type ?? '', {
    enabled: !!besluit.data?.Type,
  });
  const hoofdstukkenInEditor = useHoofdstukkenInEditor(besluitId);

  if (
    !bedrijf.data ||
    !besluit.data ||
    !besluitType.data ||
    !hoofdstukkenInEditor.data
  )
    return null;

  return (
    <>
      <DocumentTitle sections={[eStrings.LBL_DIGI_V, 'Editor']} />

      <Navigation besluit={besluit.data} bedrijf={bedrijf.data} />

      <div className="flex">
        <SidebarLeft
          besluit={besluit.data}
          hoofdstukken={hoofdstukkenInEditor.data}
        />

        <main
          className="w-full bg-gray-50 pb-10"
          // For testing purposes
          data-initieel-besluit={!!besluitType.data?.Initieel}
        >
          <Outlet />
        </main>
      </div>
    </>
  );
}
